import { daemonServiceLaneSchema } from "./schemas/daemon/service-contract.js";
import { providerDispatchRuntimeContextSchema } from "./schemas/daemon/provider-runtime-context.js";
import { openMeldDaemonCancelPayloadSchema, openMeldDaemonCancelRequestPayloadSchema, openMeldDaemonConversationExecutionStatusPayloadSchema, openMeldDaemonConversationExecutionStatusRequestPayloadSchema, openMeldDaemonInterruptPayloadSchema, openMeldDaemonInterruptRequestPayloadSchema, openMeldDaemonSetLocalAgentEnabledPayloadSchema, openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema, skippedDaemonProfileSchema } from "./schemas/daemon/control-plane.js";
import { daemonStreamFrameSchema, localWakeRouteWorkingFolderObservationSchema, runtimeTransportLeaseSchema } from "./schemas/daemon/runtime-transport.js";
import { cachedDaemonRouteConfigSchema } from "./schemas/daemon/route-catalog-cache.js";
import * as z$1 from "zod/v4";
//#region src/service-execution-host.ts
const SERVICE_EXECUTION_HOST_PROTOCOL = "service-execution-host-v1";
const SERVICE_EXECUTION_HOST_MAX_FRAME_BYTES = 8 * 1024 * 1024;
const SERVICE_EXECUTION_HOST_MAX_PENDING_REQUESTS = 64;
const identityString = z$1.string().min(1).max(1024);
const count = z$1.number().int().nonnegative();
const empty = z$1.strictObject({});
const serviceExecutionHostIdentitySchema = z$1.strictObject({
	ownerUserId: identityString,
	deviceId: identityString,
	lane: daemonServiceLaneSchema,
	daemonSessionId: identityString,
	serviceContractGeneration: count,
	sessionIdentity: identityString,
	stateRoot: z$1.string().min(1).max(4096),
	authBaseUrl: z$1.url(),
	gatewayBaseUrl: z$1.url(),
	parentPid: z$1.number().int().positive(),
	parentProcessStartedAt: identityString
});
const serviceExecutionHostSnapshotSchema = z$1.strictObject({
	pid: z$1.number().int().positive(),
	admission: z$1.enum(["open", "draining"]),
	activeCount: count,
	pendingCount: count,
	localQuiescent: z$1.boolean(),
	unacknowledgedResultCount: count,
	startupReplayPending: z$1.boolean(),
	adapterIds: z$1.array(identityString).max(256)
});
const serviceExecutionHostOwnerSchema = z$1.strictObject({
	schema: z$1.literal("service-execution-host-owner-v1"),
	hostInstanceId: z$1.uuid(),
	ownerUserId: identityString,
	deviceId: identityString,
	lane: daemonServiceLaneSchema,
	pid: z$1.number().int().positive(),
	bootIdentity: identityString.nullable(),
	localQuiescent: z$1.boolean()
});
const profileWorkingFolderFactSchema = z$1.strictObject({
	defaultProjectId: identityString.nullable(),
	localProjectBindingGeneration: identityString.nullable(),
	observedAtMs: count,
	reasonCode: z$1.enum([
		"profile.workspace.invalid",
		"profile.workspace.missing",
		"project.local_connection.folder_missing",
		"project.local_connection.invalid",
		"project.local_connection.missing"
	]).nullable(),
	source: z$1.enum(["default_project", "profile_workspace"]),
	status: z$1.enum([
		"invalid",
		"missing",
		"ready"
	])
});
const readyWorkingFolder = {
	configuredWorkspacePath: z$1.string().min(1),
	effectiveRuntimeWorkingDirectory: z$1.string().min(1),
	runtimeContext: providerDispatchRuntimeContextSchema,
	status: z$1.literal("ready"),
	workingFolderFact: profileWorkingFolderFactSchema
};
const serviceExecutionHostRouteSchema = cachedDaemonRouteConfigSchema.extend({
	agentControllerConversationId: identityString.nullable().optional(),
	workingFolderRuntime: z$1.discriminatedUnion("workingFolderSource", [z$1.strictObject({
		...readyWorkingFolder,
		configPath: z$1.string().min(1),
		workspaceMode: z$1.enum(["openmeld_managed", "custom"]),
		workingFolderSource: z$1.literal("profile_workspace")
	}), z$1.strictObject({
		...readyWorkingFolder,
		configPath: z$1.null(),
		workspaceMode: z$1.null(),
		defaultProjectId: identityString,
		defaultProjectBindingGeneration: identityString,
		workingFolderSource: z$1.literal("default_project")
	})]).optional()
});
const serviceExecutionHostCatalogSchema = z$1.strictObject({
	revision: z$1.number().int().positive(),
	expectedRoutesContractVersion: z$1.literal("v4").optional(),
	profileIds: z$1.array(identityString).max(1e4),
	routes: z$1.array(serviceExecutionHostRouteSchema).max(1e4),
	skippedProfiles: z$1.array(skippedDaemonProfileSchema).max(1e4),
	workingFolderExpectedProfileIds: z$1.array(identityString).max(1e4).optional(),
	workingFolderObservations: z$1.array(localWakeRouteWorkingFolderObservationSchema).max(1e4).optional()
}).superRefine((catalog, context) => {
	const ids = new Set(catalog.profileIds);
	const routes = new Set(catalog.routes.map((route) => route.profileId));
	if (ids.size !== catalog.profileIds.length || routes.size !== catalog.routes.length || ids.size !== routes.size || catalog.profileIds.some((id) => !routes.has(id))) context.addIssue({
		code: "custom",
		message: "Host catalog must contain exactly one route per profile."
	});
});
const reportResult = z$1.strictObject({
	status: z$1.enum(["skipped", "submitted"]),
	reasonCodes: z$1.array(z$1.string().max(256)).max(1024),
	submittedCount: count,
	failedCount: count
});
const executionFrameCodes = /* @__PURE__ */ new Set([
	"daemon.dispatch.deliver",
	"daemon.dispatch.lifecycle_acknowledged",
	"daemon.dispatch.accepted",
	"daemon.dispatch.interaction_response",
	"daemon.reasoning.preferences",
	"daemon.local_agent_control.cancel",
	"daemon.local_agent_control.interrupt",
	"daemon.local_agent_control.fork_session",
	"daemon.local_agent_control.manage_installation",
	"daemon.local_agent_control.set_enabled",
	"daemon.execution_recovery.resume_controller_conversation"
]);
const executionOutputFrameCodes = /* @__PURE__ */ new Set([
	"daemon.dispatch.result",
	"daemon.dispatch.lifecycle",
	"daemon.dispatch.runtime_evidence",
	"daemon.dispatch.reasoning",
	"daemon.dispatch.tool_activity",
	"daemon.dispatch.interaction_request",
	"daemon.dispatch.interaction_delivered",
	"daemon.dispatch.preview",
	"daemon.dispatch.preview_end",
	"daemon.local_agent_control.result",
	"daemon.execution_recovery.resume_controller_conversation_result"
]);
const serviceExecutionHostMethods = {
	initialize: {
		input: serviceExecutionHostIdentitySchema,
		output: serviceExecutionHostSnapshotSchema
	},
	health: {
		input: empty,
		output: serviceExecutionHostSnapshotSchema
	},
	loadCatalog: {
		input: z$1.strictObject({ initial: z$1.boolean() }),
		output: serviceExecutionHostCatalogSchema
	},
	configureConnection: {
		input: z$1.strictObject({
			executionAdmissionConfirmation: z$1.boolean().optional(),
			connectionGeneration: z$1.number().int().positive(),
			catalogRevision: count,
			leases: z$1.array(runtimeTransportLeaseSchema).max(1e4)
		}),
		output: serviceExecutionHostSnapshotSchema
	},
	detachConnection: {
		input: z$1.strictObject({ connectionGeneration: z$1.number().int().positive() }),
		output: serviceExecutionHostSnapshotSchema
	},
	heartbeat: {
		input: z$1.strictObject({
			connectionGeneration: z$1.number().int().positive(),
			staleProfileIds: z$1.array(identityString).max(1e4)
		}),
		output: serviceExecutionHostSnapshotSchema
	},
	handleFrame: {
		input: z$1.strictObject({
			connectionGeneration: z$1.number().int().positive(),
			frame: daemonStreamFrameSchema.refine((frame) => executionFrameCodes.has(frame.code), "This frame belongs to the connector, not the execution host.")
		}),
		output: empty
	},
	cancel: {
		input: openMeldDaemonCancelRequestPayloadSchema,
		output: openMeldDaemonCancelPayloadSchema
	},
	interrupt: {
		input: openMeldDaemonInterruptRequestPayloadSchema,
		output: openMeldDaemonInterruptPayloadSchema
	},
	conversationStatus: {
		input: openMeldDaemonConversationExecutionStatusRequestPayloadSchema,
		output: openMeldDaemonConversationExecutionStatusPayloadSchema
	},
	setEnabled: {
		input: openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema,
		output: openMeldDaemonSetLocalAgentEnabledPayloadSchema
	},
	reportControllers: {
		input: z$1.strictObject({ probePolicy: z$1.enum(["background", "fresh"]) }),
		output: reportResult.extend({
			reportCount: count,
			hasArmedReprobe: z$1.boolean()
		})
	},
	reportConversations: {
		input: empty,
		output: reportResult.extend({ proofCount: count })
	},
	persistCatalog: {
		input: z$1.strictObject({ revision: z$1.number().int().positive() }),
		output: empty
	},
	syncActivity: {
		input: z$1.strictObject({ refreshIntegrations: z$1.boolean().optional() }),
		output: empty
	},
	beginDrain: {
		input: empty,
		output: serviceExecutionHostSnapshotSchema
	},
	drain: {
		input: empty,
		output: serviceExecutionHostSnapshotSchema
	},
	settleResults: {
		input: empty,
		output: z$1.strictObject({ status: z$1.enum([
			"acknowledged",
			"connection_closed",
			"timeout"
		]) })
	},
	stop: {
		input: empty,
		output: serviceExecutionHostSnapshotSchema
	}
};
const method = z$1.enum(Object.keys(serviceExecutionHostMethods));
const envelope = {
	protocol: z$1.literal(SERVICE_EXECUTION_HOST_PROTOCOL),
	hostInstanceId: z$1.uuid()
};
const serviceExecutionHostRequestSchema = z$1.strictObject({
	...envelope,
	requestId: z$1.uuid(),
	method,
	payload: z$1.unknown()
});
const serviceExecutionHostMessageSchema = z$1.discriminatedUnion("type", [
	z$1.strictObject({
		...envelope,
		type: z$1.literal("reply"),
		requestId: z$1.uuid(),
		method,
		result: z$1.discriminatedUnion("ok", [z$1.strictObject({
			ok: z$1.literal(true),
			payload: z$1.unknown()
		}), z$1.strictObject({
			ok: z$1.literal(false),
			error: z$1.strictObject({
				code: z$1.string().min(1).max(256),
				message: z$1.string().min(1).max(4096)
			})
		})])
	}),
	z$1.strictObject({
		...envelope,
		type: z$1.literal("frame"),
		connectionGeneration: z$1.number().int().positive(),
		frame: daemonStreamFrameSchema.refine((frame) => executionOutputFrameCodes.has(frame.code), "The execution host cannot emit connector-owned frames.")
	}),
	z$1.strictObject({
		...envelope,
		type: z$1.literal("invalidate"),
		scope: z$1.enum([
			"routes",
			"activity",
			"execution"
		])
	}),
	z$1.strictObject({
		...envelope,
		type: z$1.literal("authentication_failure"),
		connectionGeneration: z$1.number().int().positive().nullable(),
		failure: z$1.strictObject({
			credentialFingerprint: z$1.string().min(1).max(256).optional(),
			message: z$1.string().min(1).max(4096),
			reasonCode: z$1.string().min(1).max(256)
		})
	})
]);
//#endregion
export { SERVICE_EXECUTION_HOST_MAX_FRAME_BYTES, SERVICE_EXECUTION_HOST_MAX_PENDING_REQUESTS, SERVICE_EXECUTION_HOST_PROTOCOL, profileWorkingFolderFactSchema, serviceExecutionHostCatalogSchema, serviceExecutionHostIdentitySchema, serviceExecutionHostMessageSchema, serviceExecutionHostMethods, serviceExecutionHostOwnerSchema, serviceExecutionHostRequestSchema, serviceExecutionHostRouteSchema, serviceExecutionHostSnapshotSchema };

//# sourceMappingURL=service-execution-host.js.map